import React, { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import { tenantHref } from "@/lib/tenantPath";
import { checkoutErrorMessage } from "@/lib/checkoutErrors";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import useFieldErrors from "../../hooks/useFieldErrors";
import { emailField, requiredField } from "../../lib/validation";
import { currentRef } from "../../lib/affiliateRef";
import { enrollInPathway, openPathwayCheckout } from "../../pathways/learnerApi";
import { checkoutBody, enrollBody, isPaid, pathwayPath } from "../../pathways/learnerModel";
import { pathwayErrorCode, pathwayErrorMessage } from "../../pathways/errors";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";

const NAME = requiredField(120);
const VISITOR_SCHEMA = z.object({ name: NAME, email: emailField });
const MEMBER_SCHEMA = z.object({ name: NAME });

const INPUT = "w-full rounded-full border border-eh-border-soft bg-eh-surface px-4 h-11 text-sm text-eh-ink outline-none focus:border-eh-ink focus:ring-2 focus:ring-eh-sage-deep/20 disabled:bg-eh-cream-deep disabled:text-eh-muted";

/**
 * "Join the pathway" (spec screen 6, "signup form"): Name, Email, Enroll.
 *
 * THE FLOW (contract §0.5, §5.2, §5.3).
 *   1. The form is judged on the press. The email rule is the shared one
 *      (lib/validation.emailField, the validator every client form uses); its refusal
 *      reads "Check your email address".
 *   2. Sign-in through the shared gate, `useRequireAuth().ensureAuthed(email, null,
 *      { prefill: true })`: the email + OTP dialog when there is no session, opened with
 *      the address this form just collected. The typed address is handed to the
 *      gate so that signing in as ANOTHER address stops this press and says which address
 *      will be used (the gate's own rule); the next press goes through with the form
 *      showing that address, locked. A visitor already signed in sees whose session it is
 *      and "Not you?" to switch, exactly as on the course player.
 *   3. Free: `POST /public/pathways/{id}/enroll {name, locale}`, then the pathway home.
 *      Paid: `POST /checkout/connect/pathway`, then the browser goes to Stripe; Stripe
 *      returns to the pathway's own page (`return_path`), which confirms the payment.
 *      `{enrolled: true}` (fully covered by a gift card or a promo) goes to the home.
 *   4. ALREADY_ENROLLED from the checkout is not an error to the learner: they have the
 *      pathway, so they are taken to it. Every other refusal is worded by
 *      `pathwayErrorMessage`, and a CAPACITY_REACHED tells the page its figures are stale.
 *
 * The wireframe's "Creates/matches CRM record · source Pathway" is an annotation for the
 * practitioner, not copy: the server does it (contract §6, CRM) and the visitor is not told.
 *
 * @param {object}   props
 * @param {object}   props.card        PublicPathwayCard or PublicPathwayDetail
 * @param {object}   props.auth        useRequireAuth() result: { user, ensureAuthed }
 * @param {(pathwayId: string) => void} props.onEnrolled  the learner now has the pathway
 * @param {() => void} [props.onFull]  after a 409 CAPACITY_REACHED
 */
export default function JoinForm({ card, auth, onEnrolled, onFull }) {
  const t = useT("public");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { logout } = useAuth();
  const { user, ensureAuthed } = auth;
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const id = card.id;

  // A visitor who signs in after the form mounted gets their name filled in, unless they
  // already typed one.
  useEffect(() => {
    if (user?.name) setName((current) => current || user.name);
  }, [user?.name]);

  const refusal = (e) => {
    const fallback = t("pathways.join.error");
    // The money path's own codes (a provider outage, a declined gift card) are worded by
    // the shared checkout map; the pathway codes by the pathway map, which wins.
    const paidFallback = isPaid(card) ? checkoutErrorMessage(e, t, fallback) : fallback;
    return pathwayErrorMessage(e, tc, paidFallback || fallback);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError("");
    // Judged BEFORE the sign-in round trip: a malformed address is this form's own
    // mistake, and must not open the dialog only to be discarded.
    if (validate(user ? MEMBER_SCHEMA : VISITOR_SCHEMA, { name, email })) return;
    const signedIn = await ensureAuthed(email.trim(), null, { prefill: true });
    if (!signedIn) return;
    setBusy(true);
    let leaving = false;
    try {
      if (isPaid(card)) {
        const result = await openPathwayCheckout(checkoutBody({
          pathwayId: id,
          name,
          locale: lang,
          originUrl: window.location.origin,
          // The pathway's own page, tenant-prefixed in path mode: it is the page that
          // confirms the payment on the way back (PathwayMember).
          returnPath: tenantHref(pathwayPath(id)),
          ref: currentRef(),
        }));
        if (result.checkout_url) {
          leaving = true;
          window.location.assign(result.checkout_url);
          return;
        }
        toast.success(t("pathways.join.enrolledToast"));
        onEnrolled(id);
        return;
      }
      const result = await enrollInPathway(id, enrollBody(name, lang));
      toast.success(result.already_enrolled ? t("pathways.join.alreadyToast") : t("pathways.join.enrolledToast"));
      onEnrolled(id);
    } catch (e) {
      const code = pathwayErrorCode(e);
      if (code === "ALREADY_ENROLLED") {
        toast.success(t("pathways.join.alreadyToast"));
        onEnrolled(id);
        return;
      }
      setError(refusal(e));
      if (code === "CAPACITY_REACHED") onFull?.();
    } finally {
      // The page is being replaced by the checkout: the button stays busy until it is.
      if (!leaving) setBusy(false);
    }
  };

  const nameError = fieldErrors.name ? tc(fieldErrors.name) : "";
  const emailError = fieldErrors.email === "validation.emailRequired"
    ? tc("validation.emailRequired")
    : fieldErrors.email ? t("pathways.join.emailInvalid") : "";

  return (
    <form
      onSubmit={submit}
      noValidate
      data-testid={IDS.joinForm(id)}
      aria-labelledby={`pathway-join-title-${id}`}
      className="rounded-3xl border border-eh-border-soft bg-eh-cream p-5 sm:p-6"
    >
      <h2 id={`pathway-join-title-${id}`} className="font-medium text-eh-ink">{t("pathways.join.title")}</h2>

      <div className="mt-4 space-y-4">
        <div>
          <label htmlFor={`pathway-join-name-${id}`} className="block text-sm text-eh-muted mb-1">{t("pathways.join.name")}</label>
          <input
            id={`pathway-join-name-${id}`}
            value={name}
            onChange={(e) => { setName(e.target.value); clearFieldError("name"); setError(""); }}
            maxLength={120}
            autoComplete="name"
            aria-invalid={Boolean(nameError)}
            data-testid={IDS.joinName(id)}
            className={`${INPUT} ${nameError ? invalidInputClass : ""}`}
          />
          <FieldError message={nameError} testId={IDS.joinNameError(id)} />
        </div>

        <div>
          <label htmlFor={`pathway-join-email-${id}`} className="block text-sm text-eh-muted mb-1">{t("pathways.join.email")}</label>
          <input
            id={`pathway-join-email-${id}`}
            type="email"
            value={user ? user.email : email}
            disabled={Boolean(user)}
            onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); setError(""); }}
            autoComplete="email"
            aria-invalid={Boolean(emailError)}
            data-testid={IDS.joinEmail(id)}
            className={`${INPUT} ${emailError ? invalidInputClass : ""}`}
          />
          <FieldError message={emailError} testId={IDS.joinEmailError(id)} />
          {user && (
            <p data-testid={IDS.joinSignedInAs(id)} className="mt-1.5 text-xs text-eh-muted">
              {t("clientAuth.signedInAs", { email: user.email })}{" "}
              <button
                type="button"
                onClick={() => logout()}
                data-testid={IDS.joinSwitchAccount(id)}
                className="underline"
              >
                {t("clientAuth.notYou")}
              </button>
            </p>
          )}
        </div>

        {error && (
          <p role="alert" data-testid={IDS.joinError(id)} className="text-sm text-rose-700">{error}</p>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid={IDS.joinSubmit(id)}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-eh-ink px-6 text-sm font-medium text-eh-cream hover:bg-eh-ink/90 disabled:opacity-60"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
          {t("pathways.join.submit")}
        </button>
      </div>
    </form>
  );
}
